'use client';

import { useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import type { RawAxiosRequestConfig } from 'axios';
import { logger } from '@/lib/logger';

/**
 * Creates a reCAPTCHA header object for API requests.
 * Returns undefined if no token provided.
 */
export const makeRecaptchaHeader = (token?: string): RawAxiosRequestConfig | undefined => {
  return token ? { headers: { 'g-recaptcha-response': token } } : undefined;
};

const RECAPTCHA_RETRY_DELAY = 1000;
const RECAPTCHA_MAX_RETRIES = 3;

/**
 * Hook wrapping Google reCAPTCHA v3 verification.
 * Must be used within a GoogleReCaptchaProvider.
 */
export function useRecaptcha() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  const getToken = useCallback(
    async (action: string = 'GUEST_PAYMENT'): Promise<string | undefined> => {
      if (!executeRecaptcha) return undefined;
      for (let attempt = 0; attempt <= RECAPTCHA_MAX_RETRIES; attempt++) {
        try {
          return await executeRecaptcha(action);
        } catch {
          if (attempt < RECAPTCHA_MAX_RETRIES) {
            await new Promise((r) => setTimeout(r, RECAPTCHA_RETRY_DELAY));
          } else {
            logger.warn('reCAPTCHA execution failed after retries', {
              feature: 'recaptcha',
              route: action,
            });
            return undefined;
          }
        }
      }
      return undefined;
    },
    [executeRecaptcha]
  );

  const getRecaptchaHeader = useCallback(
    async (action?: string): Promise<RawAxiosRequestConfig | undefined> => {
      const token = await getToken(action);
      return makeRecaptchaHeader(token);
    },
    [getToken]
  );

  return { getToken, getRecaptchaHeader, isReady: !!executeRecaptcha };
}
